import React from 'react'
import { useDispatch } from 'react-redux'
import Typography from '@mui/material/Typography'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '../common/Tooltip'

import { Appliance, Role } from 'common/api/v1/types'
import { pluralize, useConfirmationDialog } from '../../utils'
import { removeAppliances, restartAppliances } from '../../redux/actions/applianceActions'
import { EnrichedApplianceWithOwner, EnrichedUser } from '../../api/nm-types'
import { Api, AppDispatch } from '../../store'
import {
  ListApplianceFilterKey,
  makeListApplianceFilter,
  mapApplianceFilterToUrlParam,
  mapUrlParamToApplianceFilter,
} from './listApplianceFilter'
import { UrlParamFilteredSearchBar } from '../common/Filters/FilterView/FilteredSearchBar'
import { Theme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import DeleteIcon from '@mui/icons-material/Delete'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import IconButton from '@mui/material/IconButton'
interface CommonActionsProps {
  appliances: EnrichedApplianceWithOwner[]
  selected: Array<Appliance['id']>
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  user: EnrichedUser
}

const BatchRestartButton = ({
  selected,
  setSelected,
  disabledMessage,
}: {
  selected: EnrichedApplianceWithOwner[]
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  disabledMessage?: string
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const showConfirmation = useConfirmationDialog()

  const onClick = () => {
    showConfirmation(
      () => {
        dispatch(restartAppliances({ ids: selected.map(({ id }) => id) }))
        setSelected([])
      },
      'Restarting will affect all video streams on the appliances. After restart the appliances will run the latest software version. Do you want to continue?',
      selected.length === 1 ? selected[0].name : 'restart appliances',
      {
        ok: { text: 'restart' },
      },
    )
  }

  return (
    <Tooltip title={disabledMessage ?? 'Restart selected appliances'} disablePortal={false}>
      <span>
        <IconButton aria-label="batch restart" onClick={onClick} disabled={!!disabledMessage}>
          <RestartAltIcon />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const BatchRemoveButton = ({
  selected,
  setSelected,
  disabledMessage,
}: {
  selected: EnrichedApplianceWithOwner[]
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  disabledMessage?: string
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const showConfirmation = useConfirmationDialog()

  const onClick = () => {
    showConfirmation(
      () => {
        dispatch(removeAppliances(selected.map(({ id }) => id)))
        setSelected([])
      },
      'Are you sure you want to remove the selected appliances?',
      selected.length === 1 ? selected[0].name : 'remove appliances',
      {
        ok: { text: 'remove', color: 'error' },
      },
    )
  }
  return (
    <Tooltip title={disabledMessage ?? 'Remove selected appliances'} disablePortal={false}>
      <span>
        <IconButton aria-label="batch delete" onClick={onClick} disabled={!!disabledMessage}>
          <DeleteIcon />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const CommonActions = ({ selected, setSelected, appliances, user }: CommonActionsProps) => {
  const selectedAppliances = appliances.filter(({ id }) => selected.includes(id))
  const isOwnerOfAllSelected = selectedAppliances.every(({ _owner }) => _owner.id === user.group)
  const filters = makeListApplianceFilter({
    applianceApi: Api.appliancesApi,
    groupApi: Api.groupsApi,
    regionApi: Api.regionApi,
    tagApi: Api.tagApi,
  })

  return (
    <Toolbar disableGutters style={{ overflowX: 'clip' }}>
      <Box
        sx={{
          transition: (theme: Theme) =>
            theme.transitions.create('flex-shrink', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
        }}
        style={{
          flexShrink: selected.length > 0 ? 1 : 0,
          flexGrow: 1,
          flexBasis: '100%',
        }}
      >
        <UrlParamFilteredSearchBar
          urlParamCacheKey={'appliances'}
          mapUrlParamToFilterFn={mapUrlParamToApplianceFilter}
          mapFilterToUrlParamFn={mapApplianceFilterToUrlParam}
          searchTokenFilters={filters}
          rawTextFilter={filters.find((f) => f.key === ListApplianceFilterKey.name)!}
        />
      </Box>

      <Box
        sx={{
          transition: (theme: Theme) =>
            // Fade out, i.e. keep visible until contents has left the screen
            theme.transitions.create('visibility', {
              delay: selected.length === 0 ? theme.transitions.duration.shortest : 0,
            }),
        }}
        style={{
          marginLeft: 16,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          visibility: selected.length > 0 ? 'visible' : 'hidden',
        }}
      >
        <div style={{ width: 180, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="subtitle1">{pluralize(selected.length, 'appliance')} selected</Typography>
        </div>
        <BatchRemoveButton
          selected={selectedAppliances}
          setSelected={setSelected}
          disabledMessage={
            user.role === Role.super || isOwnerOfAllSelected
              ? undefined
              : 'You are not the owner of a selected appliance'
          }
        />
        <BatchRestartButton
          selected={selectedAppliances}
          setSelected={setSelected}
          disabledMessage={
            user.role === Role.super || isOwnerOfAllSelected
              ? undefined
              : 'You are not the owner of a selected appliance'
          }
        />
      </Box>
    </Toolbar>
  )
}

export default CommonActions
